import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useDeleteDemandSuppressionMaster } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useDeleteDemandSuppressionMaster'
import type { DemandSuppressionMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/types'
import { mapMasterDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

type Props = {
  isOpen: boolean
  row: DemandSuppressionMasterRow
  onClose: () => void
  onError: (message: string) => void
  errorMessage: string | undefined
}

/**
 * 需要抑制計画マスタ 削除確認ダイアログ。
 * optimistic lock エラー時は onError コールバックで親にエラーを通知する。
 */
export const DeleteDemandSuppressionPlanMasterDialog = ({
  isOpen,
  row,
  onClose,
  onError,
  errorMessage,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteDemandSuppressionMaster()

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        id: row.id,
        expectedUpdatedAt: row.updatedAt,
      })
      onClose()
      // 削除成功の toast (2026-08-28 デザイナー指摘)。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      onError(
        mapMasterDeleteError(
          t,
          err,
          'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError',
        ),
      )
    }
  }

  return (
    <ConfirmDialog
      body={
        errorMessage ??
        t(
          'page:adminOcctoMaster.demandSuppressionPlanMaster.delete.confirmBody',
          { submitterCode: row.submitterCode },
        )
      }
      confirmLabel={t(
        'page:adminOcctoMaster.demandSuppressionPlanMaster.delete.confirmLabel',
      )}
      destructive
      isLoading={isPending}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t(
        'page:adminOcctoMaster.demandSuppressionPlanMaster.delete.confirmTitle',
      )}
    />
  )
}
